import { Check, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  FEATURES_SHORT_EN,
  FEATURES_SHORT_FR,
  type BillingPeriod,
  type PlanDescriptor,
} from './plans'

interface Props {
  plan: PlanDescriptor
  billing: BillingPeriod
  isCurrent: boolean
  onChoose: (plan: PlanDescriptor) => void
  darkMode: boolean
}

/**
 * Card plan compacte utilisée dans ChangePlanView (Mode 2).
 *
 * 4 features key + prix selon billing. Badge "Recommandé" si featured.
 * Badge "Plan actuel" et bouton désactivé si isCurrent.
 */
export function PlanCardCompact({
  plan,
  billing,
  isCurrent,
  onChoose,
  darkMode,
}: Props) {
  const { t, i18n } = useTranslation()
  // Locale-aware features : pas via i18n.t (les listes sont longues et nécessitent
  // une cohérence stricte avec les libellés produit). Sélection simple par lang.
  const featuresMap =
    i18n.language?.startsWith('en') ? FEATURES_SHORT_EN : FEATURES_SHORT_FR
  const features = featuresMap[plan.key] ?? []
  const isFeatured = !!plan.featured

  const monthlyPrice =
    billing === 'yearly' ? plan.yearlyCad : plan.monthlyCad
  const priceDisplay =
    monthlyPrice != null
      ? `${monthlyPrice} CAD`
      : t('manager.billing.contactSales', 'Sur devis')
  const priceSuffix = plan.perUser
    ? t('manager.billing.perUserPerMonth', '/ utilisateur / mois')
    : t('manager.billing.perMonth', '/ mois')

  return (
    <div
      className={cn(
        'relative rounded-2xl border p-5 flex flex-col gap-4',
        isFeatured
          ? darkMode
            ? 'border-blue-500/50 bg-blue-500/5'
            : 'border-blue-300 bg-blue-50/30 shadow-sm'
          : darkMode
            ? 'border-slate-800 bg-slate-900'
            : 'border-slate-200 bg-white',
      )}
    >
      {isFeatured && (
        <Badge
          className={cn(
            'absolute -top-2.5 left-4 gap-1 text-xs',
            darkMode
              ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
              : 'bg-blue-100 text-blue-700 border-blue-200',
          )}
        >
          <Sparkles className="h-3 w-3" />
          {t('manager.billing.recommended', 'Recommandé')}
        </Badge>
      )}
      {isCurrent && (
        <Badge
          variant="secondary"
          className={cn(
            'absolute -top-2.5 right-4 text-xs',
            darkMode
              ? 'bg-slate-700 text-slate-200'
              : 'bg-slate-200 text-slate-700',
          )}
        >
          {t('manager.billing.currentPlan', 'Plan actuel')}
        </Badge>
      )}

      <div>
        <h3
          className={cn(
            'text-lg font-semibold',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          {t(`manager.billing.plans.${plan.key}.name`, plan.key)}
        </h3>
        <p
          className={cn(
            'text-sm mt-1',
            darkMode ? 'text-slate-400' : 'text-slate-500',
          )}
        >
          {t(
            `manager.billing.plans.${plan.key}.tagline`,
            // Fallback générique si la trad n'existe pas
            'Forfait Callizy',
          )}
        </p>
      </div>

      <div>
        <div className="flex items-baseline gap-1">
          <span
            className={cn(
              'text-3xl font-bold tabular-nums',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            {priceDisplay}
          </span>
          {monthlyPrice != null && (
            <span
              className={cn(
                'text-xs',
                darkMode ? 'text-slate-500' : 'text-slate-500',
              )}
            >
              {priceSuffix}
            </span>
          )}
        </div>
        {billing === 'yearly' && monthlyPrice != null && (
          <p
            className={cn(
              'text-xs mt-1',
              darkMode ? 'text-emerald-400' : 'text-emerald-600',
            )}
          >
            {t('manager.billing.yearlyDiscount', 'Facturation annuelle (-20%)')}
          </p>
        )}
      </div>

      <ul className="space-y-1.5 flex-1">
        {features.map((feat) => (
          <li
            key={feat}
            className={cn(
              'flex items-start gap-2 text-sm',
              darkMode ? 'text-slate-300' : 'text-slate-700',
            )}
          >
            <Check
              className={cn(
                'h-4 w-4 flex-shrink-0 mt-0.5',
                darkMode ? 'text-emerald-400' : 'text-emerald-600',
              )}
            />
            <span>{feat}</span>
          </li>
        ))}
      </ul>

      <Button
        type="button"
        onClick={() => onChoose(plan)}
        disabled={isCurrent || plan.isEnterprise}
        className={cn(
          'w-full',
          isFeatured && !isCurrent
            ? 'bg-blue-600 hover:bg-blue-700 text-white'
            : '',
        )}
        variant={isFeatured && !isCurrent ? 'default' : 'outline'}
      >
        {isCurrent
          ? t('manager.billing.currentPlan', 'Plan actuel')
          : plan.isEnterprise
            ? t('manager.billing.contactUs', 'Contactez-nous')
            : t('manager.billing.choosePlan', 'Choisir')}
      </Button>
    </div>
  )
}
